/*
 *   Copyright (c) 2022 Luxembourg Institute of Science and Technology
 *   All rights reserved.
 *   For licensing information see the "LICENSE" file in the root directory
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";

import { StyledSelect } from "../../../styled/Select";

import { setColumns as setColumnsChart } from "../../../../features/chart";

import { setColumns as setColumnsTable } from "../../../../features/table";
import { makeStyles, MenuItem } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  selection: {
    outline: "2px solid " + theme.palette.primary.main,
    color: theme.palette.text.main
  },
  deselection: {
    outline: "2px solid " + theme.palette.secondary.main,
    color: theme.palette.text.main

  }
}))

// const intersection = (a, b) => a.filter((v) => b.indexOf(v) !== -1);
const not = (a, b) => a.filter((v) => b.indexOf(v) === -1);
const union = (a, b) => [...a, ...not(b, a)];

function SelectVariables(props) {
  const dispatch = useDispatch();
  const classes = useStyles();

  // Props
  const { feature, apply } = props;

  // store
  const { schema } = useSelector((state) => state.dataset);
  const { selectedColumns } = useSelector((state) => {
    if (feature === "chart") return state["chart"];
    else if (feature === "table") return state["table"];
    else {
      return { selectedColumns: [] };
    }
  });

  // state
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [areEquals, setAreEquals] = useState(false);
  const [firstSelection, setFirstSelection] = useState(null);

  // effect
  useEffect(() => {
    setSelectedVariables(selectedColumns.map((s) => s.name));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedColumns.length !== selectedVariables.length)
      setAreEquals(false);
    else {
      if (selectedColumns.every((sc) => selectedVariables.includes(sc.name)))
        setAreEquals(true);
      else setAreEquals(false);
    }
  }, [selectedVariables, selectedColumns]);

  // events
  const onSelectedVariablesChange = (event) => {

    if (event.shiftKey && firstSelection !== null) {

      let nameDiff;
      if (selectedVariables.length < event.target.value.length)
        nameDiff = not(event.target.value, selectedVariables)[0];
      else nameDiff = not(selectedVariables, event.target.value)[0];

      let a = firstSelection.index;
      let b = schema.findIndex((s) => s.name === nameDiff);
      if (a > b) {
        a = b;
        b = firstSelection.index;
      }

      let shiftElements = schema.slice(a, b + 1).map((s) => s.name);
      let selectedVariablesWithShift = [];
      if (firstSelection.action === "selection")
        selectedVariablesWithShift = union(event.target.value, shiftElements);
      else
        selectedVariablesWithShift = event.target.value.filter(
          (s) => !shiftElements.includes(s)
        );

      setFirstSelection(null);
      setSelectedVariables(selectedVariablesWithShift);
    }
    else {
      let nameDiff;
      let action;
      if (selectedVariables.length < event.target.value.length) {
        action = "selection";
        nameDiff = not(event.target.value, selectedVariables)[0];
      } else {
        action = "deselection";
        nameDiff = not(selectedVariables, event.target.value)[0];
      }

      let index = schema.findIndex((s) => s.name === nameDiff);
      setFirstSelection({ index, name: nameDiff, action })
      setSelectedVariables(event.target.value);
    }
    if (!apply) onSelectedVariablesClick();
  };

  const onSelectedVariablesClick = (event) => {
    const [excluded, selected] = schema.reduce(
      (acc, column) => {
        if (selectedVariables.includes(column.name)) acc[1].push(column.name);
        else acc[0].push(column.name);
        return acc;
      },
      [[], []]
    );

    const partition = [];
    // Version without preserving order
    // partition[0] = schema.filter(s => excluded.includes(s.name))
    // partition[1] = schema.filter(s => selected.includes(s.name))

    // keep columns already existing in selectedColumns (with preserved order)
    partition[0] = schema.filter((s) => excluded.includes(s.name));
    partition[1] = selectedColumns.filter((d) => selected.includes(d.name));

    // add new columns at the end
    selected.forEach((d) => {
      if (!partition[1].find((p) => p.name === d))
        partition[1].push(schema.find((s) => s.name === d));
    });

    // dispatch columns with preserved order
    const setColumns =
      props.feature === "table" ? setColumnsTable : setColumnsChart;
    dispatch(
      setColumns({
        excludedColumns: partition[0],
        selectedColumns: partition[1],
      })
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gridGap: 8 }}>
      <StyledSelect
        variant="outlined"
        value={selectedVariables}
        onChange={onSelectedVariablesChange}
        multiple
        style={{ maxWidth: "400px", position: "relative" }}
      >
        {schema.map(column => (
          <MenuItem value={column.name} key={column.name} className={firstSelection && firstSelection.name === column.name ? classes[firstSelection.action] : null} >
            {column.name}
          </MenuItem>
        ))
        }
      </StyledSelect>
      {apply && (
        <Button
          variant="outlined"
          color="primary"
          disabled={areEquals}
          onClick={onSelectedVariablesClick}
        >
          Apply
        </Button>
      )}
    </Box>
  );
}

export default SelectVariables;
